import { EngineConfig, I18NextProvider } from "@spawn/core";
import { GoldenBonusSymbolTypes } from "@spawn/golden-bonus";
import { PageFeatureBanner, PageHeadline, PageText, useService } from "@spawn/modern-ui";
import * as React from "react";
import Url from "url-parse";

/** The page images that differ between the games. */
export interface IPageAssets {
	/** Folder of the images: `assets/pageAssets/@spawn/<packageName>`. */
	packageName: string;
	/** Inline image and banner of the golden bonus. */
	goldenBonusImage: string;
	bonusBanner: string;
	freegameReelsBanner: string;
}

export interface HeadlineProps {
	testId: string;
}

export interface TextProps {
	testId: string;
	params?: Record<string, string>;
}

export interface TextWithImagesProps {
	testId: string;
	params?: Record<string, string>;
}

export interface FeatureBannerProps {
	filename: string;
	height?: number;
}

export interface IBonusSymbol {
	symbolName: GoldenBonusSymbolTypes;
	symbolUrl: Url<string>;
	payoutValues: { label: string }[];
}

export interface IPageHelpers {
	services: { i18n: I18NextProvider; engineConfig: EngineConfig; inlineImages: Record<string, Url<string>> };
	components: {
		Headline: React.FC<HeadlineProps>;
		TextWithImages: React.FC<TextWithImagesProps>;
		Text: React.FC<TextProps>;
		FeatureBanner: React.FC<FeatureBannerProps>;
	};
	createImageUrl: (filename: string) => Url<string>;
	createBonusSymbol: (symbolType: GoldenBonusSymbolTypes, filename: string, payoutLabels: string[]) => IBonusSymbol;
}

export interface IPageHelpersFactory {
	assets: IPageAssets;
	usePageHelpers: () => IPageHelpers;
}

export const createPageHelpers = (assets: IPageAssets): IPageHelpersFactory => {
	const createImageUrl = (filename: string, baseUrl: string): Url<string> =>
		new Url(`assets/pageAssets/@spawn/${assets.packageName}/${filename}`, baseUrl);

	const createInlineImages = (baseUrl: string): Record<string, Url<string>> => {
		const imageFilenames = {
			reveal: "mystery.png",
			golden_bonus: assets.goldenBonusImage,
			scatter_bg: "scatter_bg.png",
			scatter_fg: "scatter_fg.png",
			top: "top.png",
			bonus_s: "bonus_s.png",
			bonus_m: "bonus_m.png",
			bonus_l: "bonus_l.png",
			bonus_xl: "bonus_xl.png",
		};

		return Object.fromEntries(Object.entries(imageFilenames).map(([key, filename]) => [key, createImageUrl(filename, baseUrl)]));
	};

	const usePageHelpers = (): IPageHelpers => {
		const i18n = useService(I18NextProvider);
		const engineConfig = useService(EngineConfig);
		const baseUrl = engineConfig.baseUrl ?? "";
		const inlineImages = createInlineImages(baseUrl);

		const Headline = ({ testId }: HeadlineProps): JSX.Element => <PageHeadline testId={testId} text={i18n.t(testId)} />;

		const TextWithImages = ({ testId, params = {} }: TextWithImagesProps): JSX.Element => (
			<PageText testId={testId} text={i18n.t(testId, params)} images={inlineImages} />
		);

		const Text = ({ testId, params = {} }: TextProps): JSX.Element => <PageText testId={testId} text={i18n.t(testId, params)} />;

		const FeatureBanner = ({ filename, height = 220 }: FeatureBannerProps): JSX.Element => (
			<PageFeatureBanner imageUrl={createImageUrl(filename, baseUrl)} imageHeight={height} />
		);

		return {
			services: { i18n, engineConfig, inlineImages },
			components: { Headline, TextWithImages, Text, FeatureBanner },
			createImageUrl: (filename) => createImageUrl(filename, baseUrl),
			createBonusSymbol: (symbolType, filename, payoutLabels) => ({
				symbolName: symbolType,
				symbolUrl: createImageUrl(filename, baseUrl),
				payoutValues: payoutLabels.map((label) => ({ label })),
			}),
		};
	};

	return { assets, usePageHelpers };
};
